import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {cancelOfflineTaskAsyncAction} from '../../../../srcCommon/tasks/taskActions';
import {buildDraftComment} from '../../../helpers/models/commentHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getHasCommentTombstone} from '../../featureFlags/featureFlagsSelectors';
import {popoutInternalComposerAction} from '../../panels/viewer/viewerPanelActions';
import {updateDataAction} from '../dataGroupsActions';
import {getOptimisticData} from '../dataSelectors';
import {removeCommentFromCacheAsync} from './commentsActionsHelpers';

/*
 * Action types.
 */

export enum CommentsActionsEnum {
  SENDING_COMMENT_DELETED = '@@data/comments/SENDING_COMMENT_DELETED',
  COMMENT_DELETED = '@@data/comments/COMMENT_DELETED',
}

/*
 * Types.
 */

interface ComposeCommentOptions {
  text?: string;
  topic?: string;
  teammateIds?: ReadonlyArray<number>;
}

interface SendingCommentDeletedPayload {
  commentUid: string;
}

export interface SendingCommentDeletedAction extends ActionWithPayload<SendingCommentDeletedPayload> {
  type: CommentsActionsEnum.SENDING_COMMENT_DELETED;
}

interface CommentDeletedPayload {
  commentUid: string;
  deletedAt: number;
  hasCommentTombstone: boolean;
}

export interface CommentDeletedAction extends ActionWithPayload<CommentDeletedPayload> {
  type: CommentsActionsEnum.COMMENT_DELETED;
}

/*
 * Action creators.
 */

export function composeCommentAction(options?: ComposeCommentOptions): ThunkAction<CommentModel> {
  return (dispatch, getState) => {
    const {company, userId} = getState().data;

    // Create the new comment.
    const newComment = buildDraftComment({
      urlBuilder: (uid) => buildComposeCommentUrl(company, uid),
      authorId: userId,
      ...options,
    });

    // Update this comment in the store.
    dispatch(updateDataAction({comments: [newComment]}));

    // Open a comment composer.
    dispatch(popoutInternalComposerAction(newComment.uid));

    return newComment;
  };
}

export function deleteSendingCommentAsyncAction(commentUid: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const optimisticData = getOptimisticData(state);
    const optimisticComment = optimisticData.comments.byId[commentUid];
    if (!optimisticComment) {
      return;
    }

    // Remove the comment from client-side storage.
    const clearCachePromise = removeCommentFromCacheAsync(optimisticComment);

    // Cancel any pending saves for this comment.
    const cancelSavePromise = optimisticComment.sendOfflineTaskId
      ? dispatch(cancelOfflineTaskAsyncAction(optimisticComment.sendOfflineTaskId))
      : Promise.resolve();

    // Remove the comment from the store.
    dispatch<SendingCommentDeletedAction>({
      type: CommentsActionsEnum.SENDING_COMMENT_DELETED,
      payload: {commentUid},
    });

    await Promise.all([
      clearCachePromise.catch((error) => {
        reportToBugsnag(error);
      }),
      cancelSavePromise.catch((error) => {
        reportToBugsnag(error);
      }),
    ]);
  };
}

export function commentDeletedAction(commentUid: string, deletedAt: number): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const hasCommentTombstone = getHasCommentTombstone(state);
    const action: CommentDeletedAction = {
      type: CommentsActionsEnum.COMMENT_DELETED,
      payload: {commentUid, deletedAt, hasCommentTombstone},
    };
    dispatch(action);
  };
}

/*
 * Helpers.
 */

function buildComposeCommentUrl(company: CompanyModel, uid: string) {
  return `${company.url}/conversations/new/comments/${uid}`;
}

/*
 * Union type.
 */

export type CommentsAction = SendingCommentDeletedAction | CommentDeletedAction;
